import { InlineMessage } from '@strata/react-inline-message';

import { Columns } from 'components/shared/Columns';
import { ContentBox } from 'components/shared/ContentBox';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const principles = [
    <div key="1">
        <h3>Clear</h3>
        <p>
            Focus on informing users what they need to know at that moment and nothing more. Be
            cognizant of when a user is new or confused, and stay away from being witty by being
            prescriptive. Write from the perspective of an expert who is open and humble. Stay away
            from jargon and acronyms.
        </p>
        <h4>Some places we use this principle</h4>
        <ul>
            <li>Info, error, warning, and hint messages</li>
            <li>Task instructions</li>
            <li>Input hint text</li>
        </ul>
    </div>,
    <div key="2">
        <h3>Cohesive</h3>
        <p>
            Keep key application terms and industry terms consistent across each application. Always
            use the Strata standard formats for data points such as date, time, dollars, reference
            rates, and more.
        </p>
        <h4>Some places we use this principle</h4>
        <ul>
            <li>Display of labels and data points in tables and tiles</li>
            <li>Data visualization</li>
        </ul>
    </div>,
    <div key="3">
        <h3>Lively</h3>
        <p>
            Where appropriate, deliver delight by introducing exciting new opportunities or
            celebrating success and goals. Pay particular attention to user&#39;s state of mind when
            writing to convey excitement.
        </p>
        <h4>Some places we use this principle</h4>
        <ul>
            <li>App updates and enhancements</li>
            <li>Success messages</li>
            <li>Holiday and empty state messages</li>
        </ul>
    </div>,
    <div key="4">
        <h3>Exemplary</h3>
        <p>
            Ensuring accurate information without errors is vital to an excellent user experience.
            Cross the t&#39;s and dot the i&#39;s. Go the extra step when necessary to provide
            clarification to data and ideas.
        </p>
        <h4>Some places we use this principle</h4>
        <ul>
            <li>Informational messages</li>
            <li>Feature announcements and updates</li>
        </ul>
    </div>,
    <div key="5">
        <h3>Trust</h3>
        <p>
            Building trust requires the application to be as transparent as possible. Instead of
            selling a concept we should strive to describe it from a clear and factual perspective.
        </p>
        <h4>Some places we use this principle</h4>
        <ul>
            <li>Display of data</li>
            <li>Feature announcements and updates</li>
            <li>Error, warning, and hint messages</li>
        </ul>
    </div>,
];

const activeVoice = [
    <p key="6">Using an active voice creates an engaging and dynamic tone.</p>,
    <Columns
        key="7"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <InlineMessage
                    wrapText
                    intent={InlineMessage.Intent.SUCCESS}
                    title="Use"
                    label="A capability with these attributes already exists."
                />
                <InlineMessage
                    wrapText
                    intent={InlineMessage.Intent.ERROR}
                    title="Avoid"
                    label="These attributes are already present in an existing capability."
                />
            </>
        }
    />,
];

const pleaseSorry = [
    <div key="8">
        <h3>When to use “Please” and “Sorry”</h3>
        <p>Only use “Please” when asking the user to do something inconvenient.</p>
        <ContentBox
            content={
                <InlineMessage
                    wrapText
                    intent={InlineMessage.Intent.ERROR}
                    title="Incomplete Plan"
                    label="This payer plan is currently not supported. Please process this claim through your standard vendor to avoid payment delays. "
                />
            }
        />
    </div>,
    <div key="9">
        <p>Only use “Sorry” when the system fails.</p>
        <ContentBox
            centerContent
            content={
                <img
                    style={{ width: 420 }}
                    src="https://sseasacdn.blob.core.windows.net/strata-website/voice-tone-sorry-example.svg"
                    alt="Sorry example within toast message"
                    loading="lazy"
                />
            }
        />
    </div>,
];

export const VoiceTone = () => (
    <div>
        <Page
            pageTitle="Voice & Tone"
            pageDescription={
                <p>
                    Use the Strata guidelines for voice and tone when writing content for our
                    internal and external applications.
                </p>
            }
            pageContent={
                <>
                    <Section title="Follow Basic Grammar Rules" contentArray={principles} />
                    <Section title="Active Voice" contentArray={activeVoice} />
                    <Section title="Please and Sorry" contentArray={pleaseSorry} />
                </>
            }
        />
    </div>
);
